export type KeyedRecordMergeStrategy<T> =
  | { type: 'object' }
  | { type: 'maxNumber' }
  | { type: 'custom'; merge: (existing: T, incoming: T) => T };

function mergeKeyedValues<T>({
  existing,
  incoming,
  merge,
}: {
  existing: T;
  incoming: T;
  merge: KeyedRecordMergeStrategy<T>;
}): T {
  switch (merge.type) {
    case 'object':
      return {
        ...(existing as Record<string, unknown>),
        ...(incoming as Record<string, unknown>),
      } as T;
    case 'maxNumber':
      return Math.max(existing as number, incoming as number) as T;
    case 'custom':
      return merge.merge(existing, incoming);
  }
}

export function mergeKeyedRecords<T>({
  records,
  migrateKey,
  merge,
}: {
  records: Record<string, T>;
  migrateKey: (key: string) => string;
  merge: KeyedRecordMergeStrategy<T>;
}): Record<string, T> {
  const merged: Record<string, T> = {};

  for (const [key, value] of Object.entries(records)) {
    const nextKey = migrateKey(key);
    const existing = merged[nextKey];
    merged[nextKey] =
      existing === undefined
        ? value
        : mergeKeyedValues({ existing, incoming: value, merge });
  }

  return merged;
}
